import { without } from 'es-toolkit';
import React, { useEffect, useState } from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { HelpButton } from '@paragon/client-tools';
import { formatLumberSpeciesLong, GripDesignSet, LUMBER_SPECIES, PLATE_TYPES } from '@paragon/contract-library';
import { naturalSort } from '@paragon/utilities';

import {
  addRow,
  changeCell,
  deleteRow,
  loadGripValues,
  restoreToLoaded,
  saveGripValues,
} from '../../store/plateGripDesignValuesSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { NavigationBreadcrumbsBase } from '../interface/navigation/NavigationBreadcrumbsBase';
import { LoadingPage } from './LoadingPage';

type Props = { rows: Partial<GripDesignSet>[] };

export function PlateDesignValuesPage() {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(loadGripValues());
  }, [dispatch]);

  const rows = useAppSelector((state) => state.plateGrip.current);

  const status = useAppSelector((state) => state.plateGrip.remote.status);
  if (status === 'running' || status === 'pending') return <LoadingPage />;

  return <UnconnectedPage rows={rows} />;
}

const crumbs: [string, string][] = [['Home', '/']];

const lumberSpeciesOptions = without(LUMBER_SPECIES, 'Combo', 'Unknown').toSorted(naturalSort);
const plateTypeOptions = without(PLATE_TYPES, 'Custom16', 'Custom18', 'Custom18HS', 'Custom20', 'Custom20HS').toSorted(
  naturalSort,
);

function GripValuesRow({ values, i }: { values: Partial<GripDesignSet>; i: number }) {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          '&:hover': { backgroundColor: (theme) => theme.palette.action.hover, cursor: 'pointer' },
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Select
            required
            fullWidth
            size="small"
            value={values.plate ?? ''}
            onClick={(e) => e.stopPropagation()}
            onFocus={() => !open && setOpen(true)}
            onChange={(e) => dispatch(changeCell({ column: 'plate', row: i, value: e.target.value }))}
          >
            {plateTypeOptions.map((plate) => (
              <MenuItem key={plate} value={plate}>
                {plate}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>
          <Select
            required
            fullWidth
            size="small"
            value={values.lumber ?? ''}
            onClick={(e) => e.stopPropagation()}
            onFocus={() => !open && setOpen(true)}
            onChange={(e) => dispatch(changeCell({ column: 'lumber', row: i, value: e.target.value }))}
          >
            {lumberSpeciesOptions.map((lumber) => (
              <MenuItem key={lumber} value={lumber}>
                {formatLumberSpeciesLong(lumber, [])}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>
          <TextField
            type="text"
            fullWidth
            size="small"
            required
            placeholder="Source or contact email address"
            value={values.source}
            autoComplete="off"
            onClick={(e) => e.stopPropagation()}
            onFocus={() => !open && setOpen(true)}
            onChange={(e) => dispatch(changeCell({ column: 'source', row: i, value: e.target.value }))}
          />
        </TableCell>
        <TableCell>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              dispatch(deleteRow(i));
            }}
            onFocus={() => !open && setOpen(true)}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Stack
              direction="row"
              spacing={2}
              component={Paper}
              sx={{ margin: 2, marginLeft: '85px', padding: 2, backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label="AA (psi/pair)"
                value={values.aa}
                autoComplete="off"
                onChange={(e) => dispatch(changeCell({ column: 'aa', row: i, value: e.target.value }))}
              />

              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label="EA (psi/pair)"
                value={values.ea}
                autoComplete="off"
                onChange={(e) => dispatch(changeCell({ column: 'ea', row: i, value: e.target.value }))}
              />

              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label="AE (psi/pair)"
                value={values.ae}
                autoComplete="off"
                onChange={(e) => dispatch(changeCell({ column: 'ae', row: i, value: e.target.value }))}
              />

              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label="EE (psi/pair)"
                value={values.ee}
                autoComplete="off"
                onChange={(e) => dispatch(changeCell({ column: 'ee', row: i, value: e.target.value }))}
              />

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Net Area Edge Offset (in.)"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <HelpButton>
                          <Typography variant="body2">{netAreaTooltip}</Typography>
                        </HelpButton>
                      </InputAdornment>
                    ),
                  },
                }}
                value={values.netAreaEdgeOffset}
                autoComplete="off"
                onChange={(e) => dispatch(changeCell({ column: 'netAreaEdgeOffset', row: i, value: e.target.value }))}
              />

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Net Area End Offset (in.)"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <HelpButton>
                          <Typography variant="body2">{netAreaTooltip}</Typography>
                        </HelpButton>
                      </InputAdornment>
                    ),
                  },
                }}
                value={values.netAreaEndOffset}
                autoComplete="off"
                onChange={(e) => dispatch(changeCell({ column: 'netAreaEndOffset', row: i, value: e.target.value }))}
              />
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function UnconnectedPage(props: Props) {
  const dispatch = useAppDispatch();
  return (
    <>
      <NavigationBreadcrumbsBase crumbs={crumbs} />
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ borderBottom: 1 }}>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ width: 175 }}>Plate</TableCell>
              <TableCell sx={{ width: 325 }}>Lumber</TableCell>
              <TableCell>Source</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.rows.map((row, i) => (
              <GripValuesRow key={i} i={i} values={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', mt: 2, width: '100%' }}>
        <Button variant="contained" onClick={() => dispatch(addRow())}>
          Add Row
        </Button>

        <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
          <Button variant="outlined" onClick={() => dispatch(restoreToLoaded())}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => dispatch(saveGripValues())}>
            Save
          </Button>
        </Box>
      </Box>
    </>
  );
}

const netAreaTooltip = `
    When plates are tested via the Gross Area Method, leave both net area offsets as 0.
    See TPI 5.2.6.2.1 & 5.3.6.2.2 for an explanation of "Gross Area Method" and "Net Area Method".
`;
